import React from "react";
import { Plus, X } from "lucide-react";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Button } from "./ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";

const MESES = ["Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio", "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre"];
export const CARGOS = ["Mantenimiento", "Compras", "Recepción", "Electricista", "Gerencia", "Producción", "Bodega", "Contabilidad", "Ingeniería", "Proyectos"];

export const emptyBuilderContact = { name: "", position: "", area: "", bMonth: "", bDay: "", is_primary: false, phones: [{ number: "", ext: "" }], mobiles: [""], emails: [""] };

// Convierte un contacto guardado (campos legacy o arrays) al formato del editor.
export function contactToBuilder(ct) {
  const emails = (ct.emails_list && ct.emails_list.length)
    ? ct.emails_list
    : [ct.email || "", ...((ct.emails || "").split(",").map((s) => s.trim()).filter(Boolean))].filter((v, i) => v !== "" || i === 0);
  return {
    id: ct.id, client_id: ct.client_id, is_primary: !!ct.is_primary,
    name: ct.name || "", position: ct.position || "", area: ct.area || "",
    bMonth: (ct.birthday || "-").split("-")[0] || "", bDay: (ct.birthday || "-").split("-")[1] || "",
    phones: (ct.phones && ct.phones.length) ? ct.phones : ((ct.phone || ct.ext) ? [{ number: ct.phone || "", ext: ct.ext || "" }] : [{ number: "", ext: "" }]),
    mobiles: (ct.mobiles && ct.mobiles.length) ? ct.mobiles : (ct.mobile ? [ct.mobile] : [""]),
    emails: emails.length ? emails : [""],
  };
}

// Convierte el formato del editor al payload que espera el backend.
export function builderToPayload(c, parentId, key = "client_id") {
  return {
    [key]: parentId || c[key], name: (c.name || "").trim(), position: (c.position || "").trim(),
    area: c.area || "", is_primary: !!c.is_primary,
    birthday: (c.bMonth && c.bDay) ? `${c.bMonth}-${c.bDay}` : "",
    phones: (c.phones || []).filter((p) => p.number || p.ext).map((p) => ({ number: p.number, ext: p.ext })),
    mobiles: (c.mobiles || []).filter(Boolean),
    emails_list: (c.emails || []).filter(Boolean),
  };
}

function MultiText({ label, values, onChange, placeholder, testid, addLabel }) {
  return (
    <div className="space-y-1">
      <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">{label}</Label>
      {values.map((v, i) => (
        <div key={i} className="flex gap-2">
          <Input className="rounded-sm flex-1 h-8 text-sm" placeholder={placeholder} value={v} onChange={(e) => onChange(values.map((x, j) => (j === i ? e.target.value : x)))} data-testid={`${testid}-${i}`} />
          {values.length > 1 && <button type="button" onClick={() => onChange(values.filter((_, j) => j !== i))} className="text-destructive"><X className="h-4 w-4" /></button>}
        </div>
      ))}
      <Button type="button" size="sm" variant="ghost" className="h-7 text-xs gap-1" onClick={() => onChange([...values, ""])} data-testid={`${testid}-add`}><Plus className="h-3.5 w-3.5" /> {addLabel}</Button>
    </div>
  );
}

// Editor de UN contacto (usado tanto al crear varios como al editar uno existente).
export function ContactFields({ c, onChange, cargos = CARGOS, idx = 0 }) {
  const set = (patch) => onChange({ ...c, ...patch });
  const cargoKnown = cargos.includes(c.position);
  return (
    <div className="space-y-2">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        <Input className="rounded-sm" placeholder="Nombre *" value={c.name} onChange={(e) => set({ name: e.target.value })} data-testid={`contact-name-${idx}`} />
        <Input className="rounded-sm" placeholder="Área" value={c.area} onChange={(e) => set({ area: e.target.value })} data-testid={`contact-area-${idx}`} />
        <div className="space-y-1">
          <Select value={cargoKnown ? c.position : (c.position ? "Otro" : "")} onValueChange={(v) => set({ position: v === "Otro" ? " " : v })}>
            <SelectTrigger className="rounded-sm" data-testid={`contact-cargo-${idx}`}><SelectValue placeholder="Cargo" /></SelectTrigger>
            <SelectContent>{cargos.map((x) => <SelectItem key={x} value={x}>{x}</SelectItem>)}<SelectItem value="Otro">Otro…</SelectItem></SelectContent>
          </Select>
          {!cargoKnown && c.position !== "" && <Input className="rounded-sm" placeholder="Escribe el cargo" value={c.position.trim()} onChange={(e) => set({ position: e.target.value })} data-testid={`contact-cargo-other-${idx}`} />}
        </div>
        <div className="grid grid-cols-2 gap-2">
          <Select value={c.bMonth || ""} onValueChange={(v) => set({ bMonth: v })}>
            <SelectTrigger className="rounded-sm" data-testid={`contact-bmonth-${idx}`}><SelectValue placeholder="Mes cumpleaños" /></SelectTrigger>
            <SelectContent>{MESES.map((m, i) => <SelectItem key={m} value={String(i + 1).padStart(2, "0")}>{m}</SelectItem>)}</SelectContent>
          </Select>
          <Select value={c.bDay || ""} onValueChange={(v) => set({ bDay: v })}>
            <SelectTrigger className="rounded-sm" data-testid={`contact-bday-${idx}`}><SelectValue placeholder="Día" /></SelectTrigger>
            <SelectContent>{Array.from({ length: 31 }, (_, k) => String(k + 1).padStart(2, "0")).map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      </div>
      <div className="space-y-1">
        <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">Teléfonos (con extensión)</Label>
        {c.phones.map((p, pi) => (
          <div key={pi} className="flex gap-2">
            <Input className="rounded-sm flex-1 h-8 text-sm" placeholder="Teléfono" value={p.number} onChange={(e) => set({ phones: c.phones.map((x, j) => (j === pi ? { ...x, number: e.target.value } : x)) })} data-testid={`contact-phone-${idx}-${pi}`} />
            <Input className="rounded-sm w-20 h-8 text-sm" placeholder="Ext." value={p.ext} onChange={(e) => set({ phones: c.phones.map((x, j) => (j === pi ? { ...x, ext: e.target.value } : x)) })} data-testid={`contact-ext-${idx}-${pi}`} />
            {c.phones.length > 1 && <button type="button" onClick={() => set({ phones: c.phones.filter((_, j) => j !== pi) })} className="text-destructive"><X className="h-4 w-4" /></button>}
          </div>
        ))}
        <Button type="button" size="sm" variant="ghost" className="h-7 text-xs gap-1" onClick={() => set({ phones: [...c.phones, { number: "", ext: "" }] })} data-testid={`contact-add-phone-${idx}`}><Plus className="h-3.5 w-3.5" /> Añadir teléfono</Button>
      </div>
      <MultiText label="Celulares" values={c.mobiles} onChange={(arr) => set({ mobiles: arr })} placeholder="Celular" testid={`contact-mobile-${idx}`} addLabel="Añadir celular" />
      <MultiText label="Correos" values={c.emails} onChange={(arr) => set({ emails: arr })} placeholder="Correo" testid={`contact-email-${idx}`} addLabel="Añadir correo" />
      <label className="flex items-center gap-2 text-xs cursor-pointer"><input type="checkbox" checked={c.is_primary} onChange={(e) => set({ is_primary: e.target.checked })} data-testid={`contact-primary-${idx}`} /> Contacto principal</label>
    </div>
  );
}

export function ClientContactsBuilder({ contacts, setContacts, cargos = CARGOS }) {
  const add = () => setContacts([...contacts, { ...emptyBuilderContact, phones: [{ number: "", ext: "" }], mobiles: [""], emails: [""], is_primary: contacts.length === 0 }]);
  const remove = (i) => setContacts(contacts.filter((_, idx) => idx !== i));
  return (
    <div className="space-y-3" data-testid="contacts-builder">
      {contacts.map((c, i) => (
        <div key={i} className="border border-border rounded-sm p-3 space-y-2" data-testid={`contact-builder-${i}`}>
          <div className="flex items-center justify-between">
            <span className="text-xs font-bold uppercase tracking-widest text-primary">Contacto {i + 1}</span>
            {contacts.length > 1 && <button type="button" onClick={() => remove(i)} className="text-destructive p-1" data-testid={`contact-remove-${i}`}><X className="h-4 w-4" /></button>}
          </div>
          <ContactFields c={c} idx={i} cargos={cargos} onChange={(nc) => setContacts(contacts.map((x, idx) => (idx === i ? nc : x)))} />
        </div>
      ))}
      <Button type="button" size="sm" variant="outline" className="rounded-sm gap-1" onClick={add} data-testid="add-contact-builder"><Plus className="h-4 w-4" /> Añadir contacto</Button>
    </div>
  );
}

export default ClientContactsBuilder;
